import { SelectablePill } from '@cc-shopping/ui/composites/SelectablePill';
import { SingleSelectDropdown } from '@cc-shopping/ui/composites/SingleSelectDropdown';
import { Box, Stack } from '@mui/material';
import { memo, useCallback } from 'react';
import { useFiltersAnalytics } from 'src/features/roundup-filters/store/hooks/useFiltersAnalytics';
import { useShallow } from 'zustand/shallow';
import { useOffersFilterStore } from '../../../store/Context';
import { type FilterOffersByType, FilterType } from '../../../store/enums';
import type { OffersFilterStoreState } from '../../../store/types';
import type { CardFeaturesFilterProps } from './types';
import { createApplyAndNotify } from './utils';

export const CardFeaturesFilter = memo(
  ({
    labelledById,
    options,
    forceClose,
    onAfterChange,
    showPillsOnLarge = false,
  }: CardFeaturesFilterProps) => {
    const { cardFeatureFilter, handleSingleFormUpdate, handleApplyFilters } = useOffersFilterStore(
      useShallow((state: OffersFilterStoreState) => ({
        cardFeatureFilter: state.filterFormState.cardFeatureFilter,
        handleSingleFormUpdate: state.handleSingleFormUpdate,
        handleApplyFilters: state.handleApplyFilters,
      })),
    );

    const { fireCardFeatureUpdateEvents } = useFiltersAnalytics();

    const handleChange = useCallback(
      (value: FilterOffersByType) => {
        createApplyAndNotify(
          handleSingleFormUpdate,
          handleApplyFilters,
          onAfterChange,
        )({ type: FilterType.CARD_FEATURE, value });
        fireCardFeatureUpdateEvents(value);
      },
      [handleSingleFormUpdate, handleApplyFilters, onAfterChange, fireCardFeatureUpdateEvents],
    );

    const handleFilterSelect = useCallback(
      (selectedOption: string) => {
        handleChange(selectedOption as FilterOffersByType);
      },
      [handleChange],
    );

    const handleFilterChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        handleChange(event.target.value as FilterOffersByType);
      },
      [handleChange],
    );

    if (showPillsOnLarge) {
      return (
        <Box
          sx={{
            flex: { xs: '1 1 0', lg: '0 0 auto' },
            minWidth: 0,
          }}
        >
          <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
            <SingleSelectDropdown
              {...(labelledById ? { labelledById } : { label: 'Filter by' })}
              labelPrefix={labelledById ? '' : 'Filter by: '}
              selectedOption={cardFeatureFilter}
              options={options.cardFeaturesOptions}
              onChange={handleFilterSelect}
              forceClose={forceClose}
            />
          </Box>
          <Box
            component="fieldset"
            sx={{
              border: 'none',
              margin: 0,
              minWidth: 0,
              padding: 0,
            }}
          >
            <Box
              component="legend"
              sx={{
                clip: 'rect(0 0 0 0)',
                clipPath: 'inset(50%)',
                height: '1px',
                overflow: 'hidden',
                position: 'absolute',
                whiteSpace: 'nowrap',
                width: '1px',
              }}
            >
              Filter credit cards by feature
            </Box>
            <Stack direction="row" sx={{ display: { xs: 'none', lg: 'flex' }, gap: 2 }}>
              {options.cardFeaturesOptions.map(option => (
                <SelectablePill
                  key={option.value}
                  checked={cardFeatureFilter === option.value}
                  inputType="radio"
                  label={option.label}
                  name="cardFeatures"
                  onChange={handleFilterChange}
                  value={option.value}
                />
              ))}
            </Stack>
          </Box>
        </Box>
      );
    }

    return (
      <SingleSelectDropdown
        label="Filter by"
        labelPrefix="Filter by: "
        selectedOption={cardFeatureFilter}
        options={options.cardFeaturesOptions}
        onChange={handleFilterSelect}
        forceClose={forceClose}
      />
    );
  },
);
